import AdvancedMarkdownParser from './markdownParser';
import ImageHelper from './imageHelper';

/**
 * Dynamic Blog Loader - Automatically imports all blog files
 * No manual configuration needed!
 */
export class DynamicBlogLoader {
  static blogCache = null;
  static cacheTimestamp = null;
  static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get all blog file names dynamically using Webpack's require.context
   */
  static getBlogFileNames() {
    try {
      // Use Webpack's require.context to get all .js files in blogs directory
      const blogContext = require.context('../blogs', false, /^\.\/.*\.js$/);
      const fileNames = blogContext.keys()
        .map(key => key.replace('./', ''))
        .filter(filename => filename !== 'GUIDE-BLOG.js') // Exclude guide file
        .sort((a, b) => {
          // Sort by date (newest first)
          const dateA = a.substring(0, 10);
          const dateB = b.substring(0, 10);
          return dateB.localeCompare(dateA);
        });
      
      return { fileNames, context: blogContext };
    } catch (error) {
      console.error('Error getting blog file names:', error);
      return { fileNames: [], context: null };
    }
  }

  /**
   * Load all blogs dynamically without manual imports
   */
  static async loadAllBlogs() {
    try {
      // Check cache first
      if (this.blogCache && this.cacheTimestamp && 
          (Date.now() - this.cacheTimestamp < this.CACHE_DURATION)) {
        return this.blogCache;
      }

      const { fileNames, context } = this.getBlogFileNames();
      
      if (!context || fileNames.length === 0) {
        console.warn('No blog files found, using fallback');
        return this.getFallbackBlogs();
      }

      const blogs = [];

      for (const fileName of fileNames) {
        try {
          // Dynamic import using require.context
          const blogModule = context(`./${fileName}`);
          const fileContent = blogModule.default;

          if (!fileContent) {
            console.warn(`No content found in ${fileName}`);
            continue;
          }

          const { metadata, content } = AdvancedMarkdownParser.parseMetadata(fileContent);
          
          // Extract slug from filename
          const slug = fileName.replace('.js', '');
          
          // Create blog object
          const blog = {
            id: this.generateId(slug),
            slug,
            fileName,
            metadata: {
              title: metadata.title || 'Untitled',
              description: metadata.description || '',
              date: metadata.date || new Date().toISOString().split('T')[0],
              author: metadata.author || 'Le Hong Thai',
              category: metadata.category || 'General',
              image: metadata.image || ImageHelper.getCategoryPlaceholder(metadata.category || 'General'),
              tags: metadata.tags || [],
              featured: metadata.featured || false,
              ...metadata
            },
            content,
            excerpt: this.createExcerpt(content),
            readingTime: AdvancedMarkdownParser.estimateReadingTime(content)
          };
          
          blogs.push(blog);
        } catch (error) {
          console.error(`Error loading blog file ${fileName}:`, error);
        }
      }

      // Sort blogs by date (newest first)
      const sortedBlogs = blogs.sort((a, b) => 
        new Date(b.metadata.date) - new Date(a.metadata.date)
      );

      // Cache the results
      this.blogCache = sortedBlogs;
      this.cacheTimestamp = Date.now();

      return sortedBlogs;
    } catch (error) {
      console.error('Error loading blogs:', error);
      return this.getFallbackBlogs();
    }
  }

  /**
   * Get blog count without loading all content
   */
  static getBlogCount() {
    const { fileNames } = this.getBlogFileNames();
    return fileNames.length;
  }

  /**
   * Get available blog slugs
   */
  static getAvailableSlugs() {
    const { fileNames } = this.getBlogFileNames();
    return fileNames.map(fileName => fileName.replace('.js', ''));
  }

  /**
   * Check if blog exists before loading
   */
  static async blogExists(slug) {
    const slugs = this.getAvailableSlugs();
    return slugs.includes(slug);
  }

  /**
   * Load single blog by slug
   */
  static async loadBlogBySlug(slug) {
    try {
      // Quick existence check first
      if (!(await this.blogExists(slug))) {
        return null;
      }

      const blogs = await this.loadAllBlogs();
      return blogs.find(blog => blog.slug === slug) || null;
    } catch (error) {
      console.error(`Error loading blog with slug ${slug}:`, error);
      return null;
    }
  }

  /**
   * Get featured blogs
   */
  static async getFeaturedBlogs(limit = 3) {
    try {
      const blogs = await this.loadAllBlogs();
      return blogs.filter(blog => blog.metadata.featured).slice(0, limit);
    } catch (error) {
      console.error('Error loading featured blogs:', error);
      return [];
    }
  }

  /**
   * Get blogs by category
   */
  static async getBlogsByCategory(category) {
    try {
      const blogs = await this.loadAllBlogs();
      return blogs.filter(blog => 
        blog.metadata.category.toLowerCase() === category.toLowerCase()
      );
    } catch (error) {
      console.error(`Error loading blogs for category ${category}:`, error);
      return [];
    }
  }

  /**
   * Get blogs by tag
   */
  static async getBlogsByTag(tag) {
    try {
      const blogs = await this.loadAllBlogs();
      return blogs.filter(blog => 
        blog.metadata.tags.some(blogTag => 
          blogTag.toLowerCase() === tag.toLowerCase()
        )
      );
    } catch (error) {
      console.error(`Error loading blogs for tag ${tag}:`, error);
      return [];
    }
  }

  /**
   * Search blogs
   */
  static async searchBlogs(query) {
    try {
      const blogs = await this.loadAllBlogs();
      const searchTerm = query.toLowerCase();
      
      return blogs.filter(blog => 
        blog.metadata.title.toLowerCase().includes(searchTerm) ||
        blog.metadata.description.toLowerCase().includes(searchTerm) ||
        blog.content.toLowerCase().includes(searchTerm) ||
        blog.metadata.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      );
    } catch (error) {
      console.error(`Error searching blogs with query ${query}:`, error);
      return [];
    }
  }

  /**
   * Get all categories
   */
  static async getAllCategories() {
    try {
      const blogs = await this.loadAllBlogs();
      const categories = new Set();
      blogs.forEach(blog => {
        if (blog.metadata.category) {
          categories.add(blog.metadata.category);
        }
      });
      return Array.from(categories);
    } catch (error) {
      console.error('Error getting categories:', error);
      return [];
    }
  }

  /**
   * Get all tags
   */
  static async getAllTags() {
    try {
      const blogs = await this.loadAllBlogs();
      const tags = new Set();
      blogs.forEach(blog => {
        if (blog.metadata.tags) {
          blog.metadata.tags.forEach(tag => tags.add(tag));
        }
      });
      return Array.from(tags);
    } catch (error) {
      console.error('Error getting tags:', error);
      return [];
    }
  }

  /**
   * Clear cache - useful for development
   */
  static clearCache() {
    this.blogCache = null;
    this.cacheTimestamp = null;
  }

  /**
   * Get cache status
   */
  static getCacheStatus() {
    return {
      cached: !!this.blogCache,
      timestamp: this.cacheTimestamp,
      age: this.cacheTimestamp ? Date.now() - this.cacheTimestamp : null,
      itemCount: this.blogCache ? this.blogCache.length : 0
    };
  }

  /**
   * Fallback blogs for when dynamic loading fails
   */
  static getFallbackBlogs() {
    return [
      {
        id: 'startup-success-mindset',
        slug: '20250820-startup-success-mindset',
        fileName: '20250820-startup-success-mindset.js',
        metadata: {
          title: 'Mindset Thành Công Trong Startup',
          description: 'Khám phá những tư duy quan trọng để xây dựng startup thành công từ kinh nghiệm thực tế',
          date: '2025-08-20',
          author: 'Le Hong Thai',
          category: 'Business',
          image: ImageHelper.getCategoryPlaceholder('Business'),
          tags: ['startup', 'mindset', 'business', 'success'],
          featured: true
        },
        content: '# Mindset Thành Công Trong Startup\n\nXây dựng một startup thành công không chỉ cần ý tưởng hay, mà quan trọng hơn là **mindset đúng đắn**...',
        excerpt: 'Xây dựng một startup thành công không chỉ cần ý tưởng hay, mà quan trọng hơn là mindset đúng đắn...',
        readingTime: 5
      }
    ];
  }

  /**
   * Utility functions
   */
  static generateId(slug) {
    return slug.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  }

  static createExcerpt(content, maxLength = 150) {
    // Remove markdown syntax and HTML for clean excerpt
    const plainText = content
      .replace(/#{1,6}\s/g, '') // Remove headers
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links, keep text
      .replace(/!\[[^\]]*\]\([^)]+\)/g, '') // Remove images
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/`([^`]+)`/g, '$1') // Remove inline code
      .replace(/>/g, '') // Remove blockquote markers
      .replace(/[-*]\s/g, '') // Remove list markers
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .trim();
    
    if (plainText.length <= maxLength) {
      return plainText;
    }
    
    const truncated = plainText.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    return lastSpace > 0 
      ? truncated.substring(0, lastSpace) + '...'
      : truncated + '...';
  }

  static formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
}

export default DynamicBlogLoader;